import React, { useState, useEffect, useMemo } from 'react';
import { LayoutGrid, Plus, Search, Edit, Trash2, CheckCircle, XCircle, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import TemplateFormModal from '../../components/templates/TemplateFormModal';
import { useAuth } from '../../context/AuthContext';

const TemplateCard = ({ template, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col hover:shadow-md transition-all group h-full">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-start space-x-3 max-w-[70%]">
        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-indigo-50 text-indigo-600 rounded-lg p-2">
          <FileText className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 truncate" title={template.name}>{template.name}</h3>
          <p className="text-xs text-gray-500 mt-1">
             {new Date(template.createdAt || new Date()).toLocaleDateString()}
          </p>
        </div>
      </div>
      <div className={`px-2 py-1 rounded text-xs font-medium flex items-center flex-shrink-0 ${template.status === 'ACTIVE' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
        {template.status === 'ACTIVE' ? <CheckCircle size={12} className="mr-1" /> : <XCircle size={12} className="mr-1" />}
        {template.status === 'ACTIVE' ? '启用中' : '已停用'}
      </div>
    </div>

    <div className="flex-1 space-y-3">
      <p className="text-sm text-gray-600 line-clamp-2" title={template.description}>
        {template.description || <span className="text-gray-400 italic">暂无描述</span>}
      </p>
      
      {template.visualStyle && (
        <div className="mt-2 text-[11px] text-purple-600 bg-purple-50 rounded p-1.5 border border-purple-100">
          <span className="font-semibold mr-1">🎨 视觉特征:</span> 
          <span className="line-clamp-2" title={template.visualStyle}>{template.visualStyle}</span>
        </div>
      )}
      
      <div className="flex flex-wrap gap-1 mt-2">
        {template.sceneTags ? template.sceneTags.split(',').map(tag => (
          <span key={tag} className="px-2 py-1 text-[10px] bg-gray-50 text-gray-600 border border-gray-100 rounded-md">
            {tag.trim()}
          </span>
        )) : <span className="px-2 py-1 text-[10px] bg-gray-50 text-gray-400 border border-gray-100 rounded-md">通用场景</span>}
      </div>
    </div>

    <div className="flex space-x-2 pt-4 mt-4 border-t border-gray-100">
      <button 
        onClick={() => onEdit(template)}
        className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-indigo-600 hover:border-indigo-100 text-sm font-medium flex items-center justify-center transition-colors"
      >
        <Edit size={14} className="mr-2" />
        编辑模板
      </button>
      <button 
        onClick={() => onDelete(template.id)}
        className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-100 hover:bg-red-50 transition-colors"
        title="删除"
      >
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

export default function StructureTemplatePage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Search and Pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('mine');
  const itemsPerPage = 8; // Adjust based on grid layout preference

  // Update viewMode to 'all' if user is ADMIN and was previously on 'all', or keep default
  // Just use viewMode state directly

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/templates', { params: { type: 'STRUCTURE', viewMode } });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [viewMode]);

  // Filter and paginate logic
  const filteredTemplates = useMemo(() => {
    if (!searchTerm.trim()) return templates;
    const lowerSearch = searchTerm.toLowerCase();
    return templates.filter(t => 
      t.name?.toLowerCase().includes(lowerSearch) || 
      t.description?.toLowerCase().includes(lowerSearch) ||
      t.sceneTags?.toLowerCase().includes(lowerSearch)
    );
  }, [templates, searchTerm]);

  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);
  
  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const paginatedTemplates = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredTemplates.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredTemplates, currentPage, itemsPerPage]);

  const handleOpenModal = (template = null) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingTemplate(null);
    setIsModalOpen(false);
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      if (editingTemplate) {
        await axios.put(`/api/templates/${editingTemplate.id}`, templateData);
      } else {
        await axios.post('/api/templates', templateData);
      }
      fetchTemplates();
      handleCloseModal();
    } catch (error) {
      console.error("Failed to save template:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('您确定要永久删除这个模板吗？')) {
      try {
        await axios.delete(`/api/templates/${id}`);
        fetchTemplates();
      } catch (error) {
        console.error(`Failed to delete template ${id}:`, error);
      }
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold flex items-center gap-2 text-gray-800">
          <LayoutGrid className="w-6 h-6 text-indigo-600" />
          结构模板管理
        </h1>
        <div className="flex gap-4 items-center">
          {(user?.role === 'ADMIN' || user?.permissions?.includes('page:all')) && (
            <div className="bg-gray-100 p-1 rounded-lg flex space-x-1">
              <button
                onClick={() => setViewMode('mine')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'mine' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                我的模板
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                全部模板
              </button>
            </div>
          )}
          <button onClick={() => handleOpenModal()} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium shadow-sm transition-colors">
            <Plus className="w-4 h-4" />
            新建模板
          </button>
        </div>
      </div>

      {/* Header Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6 flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input 
            type="text" 
            placeholder="搜索模板名称、描述或标签..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"
          />
        </div>
        <div className="text-sm text-gray-500">
          共 {filteredTemplates.length} 个模板
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-gray-500 flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            加载中...
          </div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-12 bg-white rounded-xl border border-gray-100 border-dashed">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
            <LayoutGrid className="w-8 h-8 text-gray-300" />
          </div>
          <h3 className="text-gray-900 font-medium mb-1">未找到模板</h3>
          <p className="text-gray-500 text-sm mb-4">
            {searchTerm ? '没有符合搜索条件的模板，请尝试其他关键词。' : '还没有创建任何结构模板，点击右上角新建。'}
          </p>
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-indigo-600 text-sm hover:underline">
              清除搜索
            </button>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
            {paginatedTemplates.map(template => (
              <TemplateCard 
                key={template.id} 
                template={template} 
                onEdit={handleOpenModal} 
                onDelete={handleDelete} 
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-200">
              <span className="text-sm text-gray-500">
                显示 {(currentPage - 1) * itemsPerPage + 1} 到 {Math.min(currentPage * itemsPerPage, filteredTemplates.length)} 条，共 {filteredTemplates.length} 条
              </span>
              <div className="flex items-center space-x-2">
                <button 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                  className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center space-x-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === i + 1 
                          ? 'bg-indigo-600 text-white' 
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                  className="p-2 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      <TemplateFormModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveTemplate}
        templateData={editingTemplate}
      />
    </div>
  );
}
